import type { ReactNode } from "react";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

export interface AdminSearchListDetailLayoutProps {
  /** 페이지 제목 */
  title: string;
  /** 페이지 설명 (h1 아래) */
  description: string;
  /** 왼쪽 패널 비율 (기본 1) */
  leftRatio?: number;
  /** 오른쪽 패널 비율 (기본 1) */
  rightRatio?: number;
  /** 검색 placeholder */
  searchPlaceholder: string;
  /** 검색 input 값 */
  searchValue: string;
  /** 검색 input 변경 */
  onSearchChange: (value: string) => void;
  /** 검색 실행 (버튼 클릭 또는 Enter) */
  onSearch: () => void;
  /** 검색 로딩 중 */
  searchLoading?: boolean;
  /** 단계 1 타이틀 (예: "1. 유저 검색") */
  step1Title: string;
  /** 단계 2 타이틀 (예: "2. 검색 결과에서 선택") */
  step2Title: string;
  /** 단계 3 타이틀 (예: "3. 이름 변경") */
  step3Title: string;
  /** 왼쪽 패널: 검색 결과 리스트 영역 */
  listContent: ReactNode;
  /** 검색 전/결과 없을 때 표시 텍스트 */
  emptyListText: string;
  /** 오른쪽 패널 내용 (선택 시), null이면 rightPanelEmptyText 표시 */
  rightPanelContent: ReactNode | null;
  /** 오른쪽 패널 비어 있을 때 표시 텍스트 */
  rightPanelEmptyText: string;
}

const DEFAULT_EMPTY_TEXT = "왼쪽 목록에서 선택하세요.";

/** 오른쪽(상세/액션) 패널 id - 모바일 단일 선택 후 스크롤 타깃용 */
export const ADMIN_SEARCH_LIST_DETAIL_RIGHT_PANEL_ID =
  "admin-search-list-detail-right-panel";

export function AdminSearchListDetailLayout({
  title,
  description,
  leftRatio = 1,
  rightRatio = 1,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onSearch,
  searchLoading = false,
  step1Title,
  step2Title,
  step3Title,
  listContent,
  emptyListText,
  rightPanelContent,
  rightPanelEmptyText = DEFAULT_EMPTY_TEXT,
}: AdminSearchListDetailLayoutProps) {
  useScrollToDetailPanelOnSelect(
    rightPanelContent,
    ADMIN_SEARCH_LIST_DETAIL_RIGHT_PANEL_ID
  );

  const gridColsClass =
    leftRatio === 7 && rightRatio === 3
      ? "lg:grid-cols-[7fr_3fr]"
      : "lg:grid-cols-2";

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSearch();
    }
  };

  return (
    <div className="w-full md:p-8">
      <h1 className="text-2xl font-bold mb-2 text-gray-800">{title}</h1>
      <p className="text-gray-600 text-sm mb-6">{description}</p>

      <div className={`grid grid-cols-1 gap-6 ${gridColsClass} items-start`}>
        {/* 왼쪽: 검색 + 결과 목록 */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* 검색 폼 */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">
              {step1Title}
            </h2>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={onSearch}
                disabled={searchLoading}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {searchLoading ? "검색 중..." : "검색"}
              </button>
            </div>
          </div>
          {/* 결과 목록 */}
          <div className="p-2 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 px-2">
              {step2Title}
            </h3>
          </div>
          <div className="min-h-96 max-h-[calc(100vh-14rem)] overflow-y-auto">
            {listContent != null ? (
              listContent
            ) : (
              <div className="p-6 text-center text-gray-500 text-sm">
                {emptyListText}
              </div>
            )}
          </div>
        </div>

        {/* 오른쪽: 단계 3 액션 패널 (모바일 단일 선택 시 스크롤 타깃) */}
        <div
          id={ADMIN_SEARCH_LIST_DETAIL_RIGHT_PANEL_ID}
          className="bg-white border border-gray-200 rounded-lg overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-800">
              {step3Title}
            </h2>
          </div>
          <div className="p-4 space-y-4">
            {rightPanelContent ?? (
              <p className="text-gray-500 text-sm">{rightPanelEmptyText}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
